import { getApiErrorMessage } from '../services/apiClient'

const FALLBACK_MESSAGE = 'Failed to update barcodes'

const LEGACY_BELONGS_TO_PRODUCT =
  /Barcode\s+"([^"]+)"\s+already belongs to product\s+"([^"]+)"\s+\(SKU:\s*([^)]+)\)/i

const ALREADY_ASSIGNED_WITH_SKU =
  /Barcode\s+"([^"]+)"\s+is already assigned to[^\n]*?\(SKU:\s*([^)]+)\)/i

const ALREADY_ASSIGNED_TO_ARTICLE =
  /Barcode\s+"([^"]+)"\s+is already assigned to article code\s+(\S+)/i

const GENERIC_DUPLICATE =
  /already exists|duplicate entry|duplicate key|unique constraint/i

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue
    const text = String(value).trim()
    if (text) return text
  }
  return ''
}

function isUnknownOwnerName(name) {
  const normalized = String(name || '').trim().toLowerCase()
  return !normalized || normalized === 'unknown product' || normalized === 'another product'
}

function isUnknownArticleCode(code) {
  const normalized = String(code || '').trim().toLowerCase()
  return !normalized || normalized === 'n/a'
}

export function formatBarcodeConflictMessage(barcode, articleCode, productName) {
  const code = isUnknownArticleCode(articleCode) ? '' : String(articleCode).trim()
  const name = isUnknownOwnerName(productName) ? '' : String(productName).trim()
  const barcodeValue = String(barcode || '').trim()

  if (barcodeValue && code && name) {
    return `Barcode "${barcodeValue}" is already assigned to article code ${code} (${name}).`
  }
  if (barcodeValue && code) {
    return `Barcode "${barcodeValue}" is already assigned to article code ${code}.`
  }
  if (barcodeValue) {
    return `Barcode "${barcodeValue}" is already assigned to another product.`
  }
  return 'This barcode is already assigned to another product. Please use a different barcode.'
}

function humanizeBarcodeErrorMessage(message) {
  if (!message || typeof message !== 'string') return ''

  const belongs = message.match(LEGACY_BELONGS_TO_PRODUCT)
  if (belongs) {
    return formatBarcodeConflictMessage(belongs[1], belongs[3], belongs[2])
  }

  const assignedWithSku = message.match(ALREADY_ASSIGNED_WITH_SKU)
  if (assignedWithSku) {
    return formatBarcodeConflictMessage(assignedWithSku[1], assignedWithSku[2])
  }

  const assignedToArticle = message.match(ALREADY_ASSIGNED_TO_ARTICLE)
  if (assignedToArticle) {
    return message
  }

  if (/already assigned to another product/i.test(message) || /already belongs to/i.test(message)) {
    return message.replace(/\bSKU\b/gi, 'article code')
  }

  return ''
}

/**
 * Map barcode-update API errors to a user-facing string.
 * Nest may return `message` as a string or array, plus optional `code` / `sku`.
 */
export function getBarcodeUpdateErrorMessage(err, fallback = FALLBACK_MESSAGE) {
  const data = err?.response?.data
  const raw = getApiErrorMessage(err, '')
  const message = typeof raw === 'string' ? raw.trim() : ''
  const errorLabel = typeof data?.error === 'string' ? data.error : ''

  const barcode = firstNonEmpty(
    data?.barcode,
    data?.existing_product?.barcode,
  )
  const articleCode = firstNonEmpty(
    data?.conflicting_article_code,
    data?.sku,
    data?.existing_product?.sku,
    data?.existing_product?.article_code,
  )
  const productName = firstNonEmpty(
    data?.conflicting_product_name,
    data?.existing_product?.product_name,
  )

  if (data?.code === 'BARCODE_ALREADY_ASSIGNED') {
    const fromMessage = humanizeBarcodeErrorMessage(message)
    if (fromMessage) return fromMessage
    if (barcode || articleCode) {
      return formatBarcodeConflictMessage(barcode, articleCode, productName)
    }
    return formatBarcodeConflictMessage()
  }

  const fromKnownShape = humanizeBarcodeErrorMessage(message)
  if (fromKnownShape) return fromKnownShape

  if (GENERIC_DUPLICATE.test(message) || GENERIC_DUPLICATE.test(errorLabel)) {
    return formatBarcodeConflictMessage(barcode, articleCode, productName)
  }

  return message || fallback
}
